import { Text, View } from '@react-pdf/renderer'
import {
  type ColumnAlign,
  inlineText,
  type MarkdownBlock,
} from 'features/knowledgeDocumentViewer/artifacts/freeformMarkdown'
import React from 'react'

import { renderInline } from './PdfInline'
import {
  pdfCellStyle,
  type PdfColumnAlign,
  type PdfStyles,
} from './PdfPrimitives'

type TableBlock = Extract<MarkdownBlock, { type: 'table' }>

const NUMERIC_CELL = /^[\s$€£()+\-,.%\d]*\d[\s$€£()+\-,.%\d]*$/
const MIN_COLUMN_WEIGHT = 6
const MAX_COLUMN_WEIGHT = 40

function columnAlignments(block: TableBlock): PdfColumnAlign[] {
  const count = Math.max(
    block.header.length,
    ...block.rows.map((row) => row.length)
  )
  return Array.from({ length: count }, (_, column) => {
    const declared: ColumnAlign = block.align[column] ?? null
    if (declared) return declared
    const cells = block.rows
      .map((row) => inlineText(row[column] ?? []).trim())
      .filter((text) => text.length > 0)
    const numeric =
      cells.length > 0 && cells.every((text) => NUMERIC_CELL.test(text))
    return numeric ? 'right' : 'left'
  })
}

function columnWidths(block: TableBlock, columnCount: number): string[] {
  const weights = Array.from({ length: columnCount }, (_, column) => {
    const longest = Math.max(
      inlineText(block.header[column] ?? []).length,
      ...block.rows.map((row) => inlineText(row[column] ?? []).length)
    )
    return Math.min(MAX_COLUMN_WEIGHT, Math.max(MIN_COLUMN_WEIGHT, longest))
  })
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  return weights.map((weight) => `${(100 * weight) / total}%`)
}

export function PdfTable({
  block,
  styles,
}: {
  block: TableBlock
  styles: PdfStyles
}): React.ReactElement {
  const aligns = columnAlignments(block)
  const widths = columnWidths(block, aligns.length)
  return (
    <View style={styles.table}>
      <View style={styles.tableHeaderRow} minPresenceAhead={24}>
        {aligns.map((align, column) => (
          <View
            key={column}
            style={pdfCellStyle({
              align,
              last: column === aligns.length - 1,
              width: widths[column] ?? '0%',
            })}>
            <Text style={styles.tableHeaderText}>
              {renderInline(styles, block.header[column] ?? [])}
            </Text>
          </View>
        ))}
      </View>
      {block.rows.map((row, rowIndex) => (
        <View key={rowIndex} style={styles.tableRow} wrap={false}>
          {aligns.map((align, column) => (
            <View
              key={column}
              style={pdfCellStyle({
                align,
                last: column === aligns.length - 1,
                width: widths[column] ?? '0%',
              })}>
              <Text style={styles.tableCellText}>
                {renderInline(styles, row[column] ?? [])}
              </Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  )
}
